import { Injectable, OnDestroy, inject } from "@angular/core";
import { CanActivate, Router, UrlTree } from "@angular/router";
import { Observable, Subject, fromEvent } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";
import { AuthService } from "@auth0/auth0-angular";
import { ToastrService } from "@sdk/lib/toastr/toastr.service";
import { TranslocoService } from "@jsverse/transloco";
import { UserIdleService } from "@sdk/lib/user-idle/user-idle.service";
import { LOGIN_PATH } from "@sdk/lib/values/shared-paths";

@Injectable({
  providedIn: "root",
})
export class IsUserIdleTimerStopForLoginPageGuard
  implements CanActivate, OnDestroy
{
  private _auth = inject(AuthService);
  private _router = inject(Router);
  private _userIdleService = inject(UserIdleService);
  private _toastr = inject(ToastrService);
  private _transloco = inject(TranslocoService);

  private destroy$ = new Subject<void>();
  private _sessionActive = false;

  constructor() {
    this._initializeSessionMonitoring();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _initializeSessionMonitoring(): void {
    fromEvent(document, "visibilitychange")
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!document.hidden) {
          this._checkLoginStatus();
        }
      });

    fromEvent(window, "focus")
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._checkLoginStatus();
      });

    fromEvent<StorageEvent>(window, "storage")
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.key === "auth0_logout" && event.newValue === "true" && this._sessionActive) {
          this._handleLogout();
        }
      });
  }

  private _checkLoginStatus(): void {
    this._auth.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
      if (!isAuthenticated && this._sessionActive) {
        this._handleLogout();
      }
    });
  }

  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._auth.isAuthenticated$.pipe(
      take(1),
      map((isAuthenticated) => {
        if (isAuthenticated) {
          this._sessionActive = true;
          this._userIdleService.stopTimer();
          this._userIdleService.resetTimer();
          this._userIdleService.stopWatching();
          this._userIdleService.startWatching();
          return true;
        } else {
          this._userIdleService.stopTimer();
          this._userIdleService.resetTimer();
          this._userIdleService.stopWatching();
          void this._router.navigate(["/", LOGIN_PATH]);
          return false;
        }
      }),
    );
  }

  private _handleLogout(): void {
    this._sessionActive = false;
    this._userIdleService.stopTimer();
    this._userIdleService.resetTimer();
    this._userIdleService.stopWatching();

    const isIntentionalLogout =
      localStorage.getItem("user_initiated_logout") === "true";
    localStorage.removeItem("user_initiated_logout");

    if (!isIntentionalLogout) {
      this._toastr.error(
        this._transloco.translate("common.sessionExpiredText"),
        this._transloco.translate("common.sessionExpiredTitle"),
        {
          timeOut: 5000,
        },
      );
    }

    void this._router.navigate(["/", LOGIN_PATH]);
  }
}
